import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import produce from 'immer';
import { merge } from 'lodash';

import { Button } from '~/components';
import FormDrawer from '~/components/FormDrawer';
import { PersonAddIcon } from '~/img';

import buildNamePath from '~/utils/buildNamePath';
import { FormItemInjectedProps } from '~/types/FormItemInjectedProps';
import { Shareholders } from '~/types/Shareholders';
import { ApplicationPayload } from '~/types/ApplicationPayload';
import { useGetApplication } from '~/controllers/wizard';
import { hasAtLeastOneDeclinedField } from '~/utils/softDeclinesFields';
import IndividualForm, { formName } from './IndividualForm';
import { shareholdersKey } from '.';
import { isEditButtonDisabled } from './utils';

const IndividualAdd: FC<Partial<FormItemInjectedProps<Shareholders>>> = (props) => {
  const { value, onChange } = props as FormItemInjectedProps<Shareholders>;
  const shareholdersFormState = { shareholders: value };

  const { t } = useTranslation('fillApplication');
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const newShareholderIndex = shareholdersFormState?.shareholders?.individuals?.length || 0;

  const onFinish = (newValues: ApplicationPayload) => {
    onChange?.(
      produce(shareholdersFormState, (draft) => {
        merge(draft, newValues);
      }).shareholders
    );
    handleClose();
  };

  const applicationQuery = useGetApplication();
  const hasIndividualsValidationWarnings = hasAtLeastOneDeclinedField(
    applicationQuery.data,
    'shareholders.individuals[]'
  );

  return (
    <>
      <Button
        id="addNewIndividualShareholderButton"
        extraLarge
        adaptive
        icon={<PersonAddIcon />}
        disabled={isEditButtonDisabled(
          applicationQuery.data,
          hasIndividualsValidationWarnings
        )}
        onClick={handleOpen}
      >
        {t('Add new Individual shareholder')}
      </Button>
      <FormDrawer
        id="individualAddDrawer"
        visible={open}
        onClose={handleClose}
        title={t('Add new Individual shareholder')}
        formName={formName}
      >
        <IndividualForm
          onFinish={onFinish}
          initialValues={shareholdersFormState}
          n={buildNamePath([shareholdersKey, 'individuals', newShareholderIndex])}
        />
      </FormDrawer>
    </>
  );
};

export default IndividualAdd;
